import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import SummaryModal from './SummaryModal';
import EnhancedBriefingModal from '../EnhancedBriefingModal';

const formatDateTime = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleString('en-US', { 
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true 
  }); // MM/DD/YYYY HH:MM AM/PM
};

const SummaryArchive = () => {
  const [summaries, setSummaries] = useState([]);
  const [summaryTotalRows, setSummaryTotalRows] = useState(0);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPerPage, setSummaryPerPage] = useState(10);
  const [summarySearch, setSummarySearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalHtml, setModalHtml] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedSummaryId, setSelectedSummaryId] = useState(null);
  const [showBriefingModal, setShowBriefingModal] = useState(false);
  const [briefingData, setBriefingData] = useState(null);
  const [error, setError] = useState('');

  const summaryColumns = [
    { name: 'Title', selector: row => row.ai_title || `Summary #${row.id}`, sortable: true },
    { 
      name: 'Generated At',
      selector: row => new Date(row.generated_at).getTime(), // sortable by timestamp
      sortable: true,
      cell: row => formatDateTime(row.generated_at) // displayed as MM/DD/YYYY HH:MM AM/PM
    },
    {
      name: 'Actions',
      cell: row => (
        <div className="d-flex gap-2 admin-actions-flex">
          <button className="btn btn-primary btn-sm" onClick={() => handleViewSummary(row.id)}>
            Email
          </button>
          <button className="btn btn-success btn-sm" onClick={() => handleViewBriefing(row.id)}>
            Briefing
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '180px'
    },
  ];

  const fetchSummaries = async (page, perPage, search) => {
    setSummaryLoading(true);
    try {
      const params = {
        draw: page,
        start: (page - 1) * perPage,
        length: perPage,
        'search[value]': search,
      };
      const res = await axios.get('/api/summaries', { params });

      // Merge articles into each summary
      const articlesById = Object.fromEntries((res.data.articles || []).map(a => [a.id, a]));
      const summariesWithArticles = (res.data.data || []).map(summary => ({
        ...summary,
        articles: (summary.top_insights || [])
          .concat(summary.day_stories || [], summary.week_stories || [])
          .map(item => articlesById[item.article_id])
          .filter(Boolean)
          // de-duplicate
          .filter((a, index, self) => index === self.findIndex(t => t.id === a.id))
      }));

      setSummaries(summariesWithArticles);
      setSummaryTotalRows(res.data.recordsTotal);
    } catch (err) {
      setError('Failed to fetch summaries.');
    }
    setSummaryLoading(false);
  };

  useEffect(() => {
    fetchSummaries(summaryPage, summaryPerPage, summarySearch);
    // eslint-disable-next-line
  }, [summaryPage, summaryPerPage, summarySearch]);

  const handleSummaryPageChange = page => setSummaryPage(page);
  
  const handleSummaryPerRowsChange = newPerPage => {
    setSummaryPerPage(newPerPage);
    setSummaryPage(1);
  };
  
  const handleSummarySearch = e => {
    setSummarySearch(e.target.value);
    setSummaryPage(1);
  };

  const handleViewSummary = async (id) => {
    setSelectedSummaryId(id);
    setShowModal(true);
    setModalLoading(true);
    try {
      const res = await axios.get(`/api/summaries/${id}/email_preview`);
      setModalHtml(res.data || res);
    } catch {
      setModalHtml('<div style="padding:2em">Failed to load email preview.</div>');
    }
    setModalLoading(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalHtml('');
    setSelectedSummaryId(null);
  };

  const handleViewBriefing = (id) => {
    const summary = summaries.find(s => s.id === id);
    if (!summary) {
      setBriefingData({ error: 'Briefing data not found.' });
      setShowBriefingModal(true);
      return;
    }

    // Map top insights, day/week stories, and article details into one object
    const articlesById = Object.fromEntries((summary.articles || []).map(a => [a.id, a]));

    const enrich = (arr) => (arr || []).map(item => ({
      ...item,
      article: articlesById[item.article_id] || null
    }));

    setBriefingData({
      ai_title: summary.ai_title,
      top_insights: enrich(summary.top_insights),
      day_stories: enrich(summary.day_stories),
      week_stories: enrich(summary.week_stories),
      articles: summary.articles
    });

    setSelectedSummaryId(id);
    setShowBriefingModal(true);
  };

  const handleCloseBriefing = () => {
    setShowBriefingModal(false);
    setBriefingData(null);
    setSelectedSummaryId(null);
  };

  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      <input 
        type="text" 
        placeholder="Search..." 
        value={summarySearch} 
        onChange={handleSummarySearch} 
        className="form-control mb-3" 
      />
      <DataTable
        columns={summaryColumns}
        data={summaries}
        progressPending={summaryLoading}
        pagination
        paginationServer
        paginationTotalRows={summaryTotalRows}
        onChangePage={handleSummaryPageChange}
        onChangeRowsPerPage={handleSummaryPerRowsChange}
        highlightOnHover
        striped
        dense
      />
      <SummaryModal 
        show={showModal}
        summaryId={selectedSummaryId}
        modalHtml={modalHtml}
        modalLoading={modalLoading}
        onClose={handleCloseModal}
      />
      <EnhancedBriefingModal 
        show={showBriefingModal}
        briefingData={briefingData}
        summaryId={selectedSummaryId}
        singleStory={null}
        isPublic={false}
        authUser={null}
        onClose={handleCloseBriefing}
      />
    </>
  );
};

export default SummaryArchive;